import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State } from '@state/core.state';
import { catchError, map, mergeMap, of, take, debounceTime } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';

import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { ProjectHelperService } from '@service/projectHelper/project-helper.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { ProjectDetailsService } from '@service/projectDetails/project-details.service';
import { CARGOCHIEF_KEYS, RANGE_FILTERS } from '@domain/constants/app-variables';
import { environment } from '@environment/environment';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { CapacitySearchFilterService } from '@service/capacitySearchFilter/capacity-search-filter.service';
import {
	DEFAULT_SYSTEM_GROUP_NAME,
	DEFAULT_SYSTEM_GROUP_DELAY,
	PROJECT_TYPES,
} from '@domain/constants/project-types';

import {
	RestService,
	IS_NOT_NULL_OR_UNDEFINED,
	PROJECT_CARRIER_MAP_STATUS,
	ProjectGroup,
	IS_NULL_OR_UNDEFINED,
	TruckSearchTable,
	IS_NOT_EMPTY,
	ProjectCarrierMapDTO,
	DELAY_OPTIONS,
	IS_EMPTY,
} from 'cargo-chief-commons';

import { ProjectDetailsStore } from '../store/project-details.store';
import { projectDetailsPageStateKey, ProjectDetailsState } from '../state';

import {
	PROJECT_DETAILS,
	PROJECT_CARRIER_MAP,
	PROJECT_GROUPS,
	RECOMMENDED_CARRIERS,
} from '../actions';

@Injectable({
	providedIn: 'root',
})
export class ProjectDetailsEffects {
	constructor(
		private actions$: Actions<any>,
		private readonly store: Store<State>,
		public readonly projectDetailsStore: ProjectDetailsStore,
		private readonly restService: RestService,
		private readonly urlService: UrlService,
		public utilService: UtilService,
		private dialog: MatDialog,
		public projectHelperService: ProjectHelperService,
		public cargochief: Cargochief,
		private projectDetailsService: ProjectDetailsService,
		public accountSettings: AccountSettingsService,
		private capacitySearchFilterService: CapacitySearchFilterService
	) {}

	getProject$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_DETAILS.PROJECT.GET),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				const url = this.urlService.getProjectById(projectDetailsPageState.projectId);
				return this.restService.GET(url).pipe(
					map((response: any) => {
						let projectDTO = this.projectHelperService.convertProjectCentsToDollars(response.data);
						let searchTruckForm: any = this.projectDetailsService.getSearchTruckForm(projectDTO);
						const frequencyFilter = this.cargochief.getItem(CARGOCHIEF_KEYS.FREQUENCY_FILTER);
						if (IS_NOT_NULL_OR_UNDEFINED(frequencyFilter)) {
							const { minLoadsPerWeek } = JSON.parse(frequencyFilter);
							searchTruckForm.minLoadsPerWeek = minLoadsPerWeek;
						}
						this.store.dispatch(
							PROJECT_DETAILS.PROJECT.SET_CAPACITY_SEARCH_FORM({ searchTruckForm: searchTruckForm })
						);
						const customerOnlyProject =
							IS_EMPTY(projectDTO.originCity) &&
							IS_EMPTY(projectDTO.originState) &&
							IS_EMPTY(projectDTO.destCity) &&
							IS_EMPTY(projectDTO.destState) &&
							IS_NOT_NULL_OR_UNDEFINED(projectDTO.customerDTO);
						this.store.dispatch(
							PROJECT_DETAILS.SET.CUSTOMER_ONLY_PROJECT({
								customerOnlyProject: customerOnlyProject,
							})
						);
						return PROJECT_DETAILS.PROJECT.SUCCESS({ projectDTO });
					}),
					catchError((error) =>
						of(PROJECT_DETAILS.PROJECT.FAILURE({ payload: { message: error.error } }))
					)
				);
			})
		)
	);

	getAllProjects$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_DETAILS.PROJECT.GET_ALL),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				const requestDTO = {
					accountId: this.cargochief.ACCOUNT_ID,
					page: projectDetailsPageState.projectsPage,
					pageSize: projectDetailsPageState.projectsPageSize,
					sortColumn: 'created',
					sortOrder: 'desc',
				};
				return this.restService.POST(this.urlService.getProjectsList(), requestDTO).pipe(
					map((response: any) => {
						this.store.dispatch(
							PROJECT_DETAILS.PROJECT.SET_ALL_PROJECTS_LIST({
								list: projectDetailsPageState.allProjectsList.concat(response.data),
							})
						);
						this.store.dispatch(
							PROJECT_DETAILS.PROJECT.SET_PROJECTS_NEXT_PAGE({
								projectsNextPage: response.nextPage,
							})
						);
						if (response.nextPage) {
							this.store.dispatch(
								PROJECT_DETAILS.PROJECT.SET_PROJECTS_PAGE({
									page: projectDetailsPageState.projectsPage + 1,
								})
							);
						}
						this.store.dispatch(
							PROJECT_DETAILS.PROJECT.SET_TOTAL_PROJECTS({ totalElements: response.totalElements })
						);
						return PROJECT_DETAILS.PROJECT.SUCCESS({});
					}),
					catchError((error) =>
						of(PROJECT_DETAILS.PROJECT.FAILURE({ payload: { message: error.error } }))
					)
				);
			})
		)
	);

	setEngagedCarriers(projectCarrierMapsList) {
		// engaged carriers
		let engagedCarriersList = [];
		if (environment.ENABLE_ENGAGED_TAB_ON_PROJECT_DETAILS) {
			engagedCarriersList = projectCarrierMapsList.filter(
				(pcm) => pcm.status === PROJECT_CARRIER_MAP_STATUS.IN_PROGRESS
			);
		}
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.ENGAGED_CARRIERS_COUNT({
				total: engagedCarriersList.length,
			})
		);
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.ENGAGED_CARRIERS_LIST({
				list: engagedCarriersList,
			})
		);
	}

	setProcuredCarriers(projectCarrierMapsList) {
		let procuredCarriersList = [];
		if (this.accountSettings.PROJECT_GROUPS_ENABLED) {
			procuredCarriersList = projectCarrierMapsList.filter(
				(pcm) =>
					pcm.status === PROJECT_CARRIER_MAP_STATUS.PROCURED &&
					IS_NULL_OR_UNDEFINED(pcm.projectGroup)
			);
		} else {
			procuredCarriersList = projectCarrierMapsList.filter(
				(pcm) => pcm.status === PROJECT_CARRIER_MAP_STATUS.PROCURED
			);
		}
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.PROCURED_CARRIERS_LIST({
				list: procuredCarriersList,
			})
		);
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.PROCURED_CARRIERS_COUNT({
				total: projectCarrierMapsList.filter(
					(pcm) => pcm.status === PROJECT_CARRIER_MAP_STATUS.PROCURED
				).length,
			})
		);
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.PROCURED_CARRIERS_WITHOUT_COUNT({
				total: procuredCarriersList.length,
			})
		);
	}

	setProjectGroups(projectCarrierMapsList) {
		let projectGroups = [];
		if (this.accountSettings.PROJECT_GROUPS_ENABLED) {
			const projectsWithGroup = projectCarrierMapsList.filter(
				(pcm) =>
					pcm.status === PROJECT_CARRIER_MAP_STATUS.PROCURED &&
					IS_NOT_NULL_OR_UNDEFINED(pcm.projectGroup)
			);
			projectGroups = projectsWithGroup
				.map((pcm) => pcm.projectGroup)
				.filter(
					(value: ProjectGroup, index, self) => index === self.findIndex((t) => t.id === value.id)
				)
				.map((pcm) => Object.assign({}, pcm))
				.sort((a, b) => (a.delayByMinutes > b.delayByMinutes ? 1 : -1));
			projectGroups.forEach((group) => {
				let groupPCMList = projectsWithGroup
					.filter((pcm) => pcm.projectGroup?.id === group.id)
					.map((pcm) => Object.assign({}, pcm));
				group.projectCarrierMapsList = groupPCMList;
				group.numberOfCarriers = group.projectCarrierMapsList.length;
				group.trucksCount = group.projectCarrierMapsList
					.map((pcm) => pcm.carrier.powerUnits || 0)
					.reduce((prev, next) => prev + next);
				group.lanesCount = group.projectCarrierMapsList
					.map((pcm) => pcm.carrier.laneCount || 0)
					.reduce((prev, next) => prev + next);
			});
		}
		this.store.dispatch(
			PROJECT_GROUPS.SET.PROJECT_GROUPS({
				list: projectGroups,
			})
		);
	}

	setCapacitySearchMatchesList(truckSearchResult) {
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(take(1))
			.subscribe((state) => {
				let capacitySearchResults = this.getAllUniqueCapacitySearchResults(
					state.allCapacitySearchResults,
					state.allProjectCarrierMapsList,
					state.powerUnits
				);
				capacitySearchResults = capacitySearchResults.splice(
					0,
					environment.NO_OF_SEARCH_RESULTS_TO_DISPLAY
				);
				this.store.dispatch(
					RECOMMENDED_CARRIERS.SET.TRUCK_SEARCH_RESULT({ list: truckSearchResult })
				);

				this.setCapacitySearchResults(capacitySearchResults);
			});
	}

	createOrUpdateSystemGroup(projectCarrierMapIds: string[]) {
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(take(1))
			.subscribe((state) => {
				if (projectCarrierMapIds.length) {
					// add carrier maps to system group if exist or else create system group
					this.store
						.select((state) => state[projectDetailsPageStateKey])
						.pipe(take(1))
						.subscribe((state) => {
							let existingSystemGroup = state.projectGroups.find((group) => group.systemGroup);
							if (existingSystemGroup) {
								let delayByMinutes = existingSystemGroup.delayByMinutes;
								const projectGroups = state.projectGroups.filter((group) => !group.systemGroup);
								if (projectGroups.length > 0) {
									delayByMinutes =
										(projectGroups[projectGroups.length - 1]?.delayByMinutes || 0) +
										DEFAULT_SYSTEM_GROUP_DELAY;
								}
								this.store.dispatch(
									PROJECT_GROUPS.UPDATE({
										systemGroup: true,
										projectCarrierMapIdList: projectCarrierMapIds.concat(
											existingSystemGroup.projectCarrierMapsList.map((pcm) => pcm.id)
										),
										name: existingSystemGroup.name,
										projectId: state.projectId,
										delayByMinutes,
										projectGroupId: existingSystemGroup.id,
									})
								);
							} else {
								// delayByMinutes should be 30 mins after lowestPriority group
								const delayByMinutes =
									(state.projectGroups[state.projectGroups.length - 1]?.delayByMinutes || 0) +
									DEFAULT_SYSTEM_GROUP_DELAY;
								const systemGroupRequest = {
									systemGroup: true,
									projectCarrierMapIdList: projectCarrierMapIds,
									name: DEFAULT_SYSTEM_GROUP_NAME,
									projectId: state.projectId,
									delayByMinutes,
								};
								this.store.dispatch(PROJECT_GROUPS.CREATE(systemGroupRequest));
							}
						});
				}
			});
	}

	sortSearchList(sortColumn: string) {
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(take(1))
			.subscribe((state) => {
				let capacitySearchResults = [].concat(state.capacitySearchResults);
				capacitySearchResults = this.capacitySearchFilterService.sortByColumn(
					sortColumn,
					capacitySearchResults
				);
				this.store.dispatch(
					RECOMMENDED_CARRIERS.SET.CAPACITY_SEARCH_RESULT({ list: capacitySearchResults })
				);
			});
	}

	filterCarriers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_CARRIER_MAP.FILTER.APPLY),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				let powerUnits = projectDetailsPageState.powerUnits,
					minimumFrequency = projectDetailsPageState.minLoadsPerWeek,
					myCarriers = projectDetailsPageState.myCarriers,
					ratesOnly = projectDetailsPageState.ratesOnly,
					mc = projectDetailsPageState.carrierMc,
					dot = projectDetailsPageState.carrierDot,
					businessName = projectDetailsPageState.carrierBusinessName;
				let projectCarrierMapsList = projectDetailsPageState.allProjectCarrierMapsList.filter(
						(pcm) => pcm.carrier.powerUnits >= powerUnits
					),
					capacitySearchResults = this.getAllUniqueCapacitySearchResults(
						projectDetailsPageState.allCapacitySearchResults,
						projectDetailsPageState.allProjectCarrierMapsList,
						projectDetailsPageState.powerUnits
					);
				if (projectDetailsPageState.carrierHQ) {
					capacitySearchResults = capacitySearchResults.filter(
						(search) => projectDetailsPageState.matchedCarrierHqIds.indexOf(search.carrierId) !== -1
					);
				}

				if (myCarriers) {
					projectCarrierMapsList = projectCarrierMapsList.filter(
						(pcm) => pcm.carrier.customerCarrier
					);
					capacitySearchResults = capacitySearchResults.filter((search) => search.customerCarrier);
				}
				if (ratesOnly) {
					projectCarrierMapsList = projectCarrierMapsList.filter((pcm) => pcm.desiredPrice > 0);
				}
				if (IS_NOT_EMPTY(mc)) {
					projectCarrierMapsList = projectCarrierMapsList.filter(
						(pcm) =>
							IS_NOT_NULL_OR_UNDEFINED(pcm.carrier.mc) &&
							pcm.carrier.mc.toLowerCase().includes(mc.toLowerCase())
					);
					capacitySearchResults = capacitySearchResults.filter(
						(search) =>
							IS_NOT_NULL_OR_UNDEFINED(search.firmMc) &&
							search.firmMc.toLowerCase().includes(mc.toLowerCase())
					);
				}
				if (IS_NOT_EMPTY(dot)) {
					projectCarrierMapsList = projectCarrierMapsList.filter(
						(pcm) =>
							IS_NOT_NULL_OR_UNDEFINED(pcm.carrier.dot) &&
							pcm.carrier.dot.toLowerCase().includes(dot.toLowerCase())
					);
					capacitySearchResults = capacitySearchResults.filter(
						(search) =>
							IS_NOT_NULL_OR_UNDEFINED(search.firmDot) &&
							search.firmDot.toLowerCase().includes(dot.toLowerCase())
					);
				}
				if (IS_NOT_EMPTY(businessName)) {
					projectCarrierMapsList = projectCarrierMapsList.filter(
						(pcm) =>
							IS_NOT_NULL_OR_UNDEFINED(pcm.carrier.businessName) &&
							pcm.carrier.businessName.toLowerCase().includes(businessName.toLowerCase())
					);
					capacitySearchResults = capacitySearchResults.filter(
						(search) =>
							IS_NOT_NULL_OR_UNDEFINED(search.businessName) &&
							search.businessName.toLowerCase().includes(businessName.toLowerCase())
					);
				}

				this.setProcuredCarriers(projectCarrierMapsList);
				this.setEngagedCarriers(projectCarrierMapsList);
				this.setProjectGroups(projectCarrierMapsList);

				this.setCapacitySearchResults(capacitySearchResults);

				return of(PROJECT_CARRIER_MAP.FILTER.COMPLETE({}));
			})
		)
	);

	setCapacitySearchResults(capacitySearchResults: TruckSearchTable[]) {
		this.store.dispatch(
			RECOMMENDED_CARRIERS.SET.CAPACITY_SEARCH_RESULT({ list: capacitySearchResults })
		);
		this.store.dispatch(
			RECOMMENDED_CARRIERS.SET.NUMBER_OF_LANES({ count: capacitySearchResults.length })
		);
		this.store.dispatch(
			RECOMMENDED_CARRIERS.SET.BEST_MATCH({
				bestMatch: this.capacitySearchFilterService.getMaxMatch(capacitySearchResults),
			})
		);
	}

	getAllUniqueCapacitySearchResults(
		allCapacitySearchResults: TruckSearchTable[],
		allProjectCarrierMapsList: ProjectCarrierMapDTO[],
		powerUnits
	) {
		let capacitySearchResults = allCapacitySearchResults.filter(
			(search) => search.powerUnits >= powerUnits
		);
		const carrierIdsInProject: string[] = allProjectCarrierMapsList.map((pcm) => pcm.carrier.id);
		capacitySearchResults = capacitySearchResults.filter(
			(search) => carrierIdsInProject.indexOf(search.carrierId) == -1
		);
		return capacitySearchResults;
	}

	moveCarriersToGroupsWhenCarriersProcured(
		projectDetailsPageState: ProjectDetailsState,
		procuredCarriersResponse: ProjectCarrierMapDTO[]
	) {
		// check if any group  exist
		const isGroupExist: boolean = projectDetailsPageState.projectGroups.length > 0;
		if (isGroupExist) {
			// add new carriers added to system group
			const systemGroup = projectDetailsPageState.projectGroups.find((group) => group.systemGroup);
			this.createOrUpdateSystemGroup(procuredCarriersResponse.map((pcm) => pcm.id));
		} else {
			let highestPriorityGroupRequest = {
				projectCarrierMapIdList: procuredCarriersResponse.map((pcm) => pcm.id),
				name: 'Priority 1',
				projectId: projectDetailsPageState.projectId,
				delayByMinutes: [].concat(DELAY_OPTIONS).sort((a, b) => (a.value > b.value ? 1 : -1))[0]
					.value,
			};
			// for project type waterfall and dedicated lane if no group exist create highest priority group
			if (
				[PROJECT_TYPES.WATERFALL.value, PROJECT_TYPES.DEDICATED_LANE.value].indexOf(
					projectDetailsPageState.projectDTO.type
				) !== -1
			) {
				this.store.dispatch(
					PROJECT_GROUPS.GROUP_AND_DEFAULT_GROUP.CREATE(highestPriorityGroupRequest)
				);
			}
		}
	}

	async setCapacitySearchDataForProjectCarrierMap() {
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(take(1), debounceTime(200))
			.subscribe((state) => {
				if (state.allCapacitySearchResults.length && state.allProjectCarrierMapsList.length) {
					let allProjectCarrierMapsList = state.allProjectCarrierMapsList.map((pcm) =>
						Object.assign({}, pcm)
					);
					allProjectCarrierMapsList.forEach((pcm) => {
						if (IS_EMPTY(pcm.frequency)) {
							const searchTruck = state.allCapacitySearchResults.find(
								(search) => search.carrierId === pcm.carrier.id
							);
							if (searchTruck) {
								pcm.frequency = searchTruck.frequency;
								pcm.originDeadhead = searchTruck.originDeadhead;
								pcm.destinationDeadhead = searchTruck.destinationDeadhead;
								pcm.lanePath = searchTruck.lanePath;
								pcm.matchScore = searchTruck.match;
							}
						}
					});
					this.setProcuredCarriers(allProjectCarrierMapsList);
					this.setEngagedCarriers(allProjectCarrierMapsList);
					this.setProjectGroups(allProjectCarrierMapsList);
					this.store.dispatch(
						PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
							list: allProjectCarrierMapsList,
						})
					);
				}
			});
	}

	openBADialog$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_DETAILS.BA.OPEN_DIALOG),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request) => {
				const projectDTO = request[1].projectDTO;
				let accountCarrierMapCount: any = this.cargochief.getItem(
					CARGOCHIEF_KEYS.ACCOUNT_CARRIER_MAP_COUNT
				);

				this.projectDetailsService.openBADialog(accountCarrierMapCount, projectDTO);
				return of(PROJECT_DETAILS.BA.CLOSE_DIALOG({}));
			})
		)
	);
}
